import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSocketContext } from "@/shared/hooks";

// Jumping dots animation component
function JumpingDots() {
    return (
        <span className="jumping-dots">
            <span className="dot">.</span>
            <span className="dot">.</span>
            <span className="dot">.</span>
            <style>{`
                .jumping-dots {
                    display: inline-flex;
                    margin-left: 1px;
                }
                .jumping-dots .dot {
                    animation: jump 1.4s ease-in-out infinite;
                    font-weight: bold;
                }
                .jumping-dots .dot:nth-child(1) { animation-delay: 0s; }
                .jumping-dots .dot:nth-child(2) { animation-delay: 0.2s; }
                .jumping-dots .dot:nth-child(3) { animation-delay: 0.4s; }
                @keyframes jump {
                    0%, 60%, 100% { transform: translateY(0); }
                    30% { transform: translateY(-4px); }
                }
            `}</style>
        </span>
    );
}

function ViewablePoolContent() {
  const { sessionID } = useParams<{ sessionID?: string }>();
  const { lobbyUpdateData, setLobbyUpdateData } = useAppSocketContext();
  const [lobby, setLobby] = useState<string[]>([]);
  const [releaseTime, setReleaseTime] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!sessionID) {
      return;
    }

    const fetchLobby = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/getLobby?tournamentId=${sessionID}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
        if (!response.ok) {
          throw new Error("Failed to fetch lobby");
        }

        const data = await response.json();
        setLobby(Array.isArray(data.usernames) ? data.usernames : []);
        setReleaseTime(data.releaseTime || null);
      } catch (error) {
        console.error("Failed to fetch pool lobby", error);
      }
    };

    fetchLobby();
  }, [sessionID]);

  useEffect(() => {
    if (!sessionID || !lobbyUpdateData || lobbyUpdateData.tournamentId !== Number(sessionID)) {
      return;
    }
    setLobby(lobbyUpdateData.usernames || []);
    setReleaseTime(lobbyUpdateData.releaseTime || null);
    setLobbyUpdateData(null);
  }, [lobbyUpdateData, sessionID, setLobbyUpdateData]);

  useEffect(() => {
    if (!releaseTime) {
      setTimeLeft("");
      return;
    }

    const interval = setInterval(() => {
      const diff = new Date(releaseTime).getTime() - Date.now();

      if (diff <= 0) {
        setTimeLeft("STARTING_SOON");
        return;
      }

      const hours = String(Math.floor(diff / (1000 * 60 * 60))).padStart(2, "0");
      const minutes = String(Math.floor((diff / (1000 * 60)) % 60)).padStart(2, "0");
      const seconds = String(Math.floor((diff / 1000) % 60)).padStart(2, "0");
      setTimeLeft(`${hours}:${minutes}:${seconds}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [releaseTime]);

  return (
    <div className="viewContainer">
      <div className="header" style={{ padding: 0, width: "100%" }}>
        <div className="standingHeaderButton">
          <span>Pool Lobby</span>
        </div>
      </div>
      <div className="header lobbyTime" style={{ padding: 0, width: "100%" }}>
        {timeLeft ? (
          timeLeft === "STARTING_SOON" ? (
            <span>Starting soon<JumpingDots /></span>
          ) : (
            timeLeft
          )
        ) : (
          <div className="loader-dots">
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}
      </div>
      <div className="scrollContainer">
        <div className="users">
          <div>
            {lobby.length ? (
              lobby.map((username, index) => (
                <div className="entry" key={`${username}-${index}`}>
                  <div className="place">#{index + 1}</div>
                  <div className="entryText">
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <h3 style={{ margin: 0 }} className="text-overflow">
                        {username}
                      </h3>
                    </div>
                    <div className="score">
                      <div className="textUppercase">0/0</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="lobbyEmpty">No pool participants yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewablePoolContent;
